import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Check, Sparkles, Zap, Settings } from 'lucide-react';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { usePayments } from '@/hooks/usePayments';
import { RoleUpgradeNotice } from './RoleUpgradeNotice';

interface SubscriptionTier {
  id: string;
  tier_name: string;
  monthly_price: number;
  monthly_credits_included: number;
  signup_bonus_credits: number;
  credits_rate_multiplier: number;
  description: string | null;
  stripe_price_id: string | null;
}

interface UserSubscription {
  tier: string;
  credits_per_min: number;
}

export const UserSubscriptionPanel = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { upgradeSubscription, manageSubscription, isProcessing } = usePayments();
  const [upgradingToTier, setUpgradingToTier] = useState<string | null>(null);

  // Fetch subscription tiers
  const { data: tiers = [], isLoading: tiersLoading } = useQuery({
    queryKey: ['subscription-tiers'],
    queryFn: async () => {
      const { data, error } = await backendClient
        .from('subscription_tiers')
        .select('*')
        .order('monthly_price', { ascending: true });

      if (error) throw error;
      return data as SubscriptionTier[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch user's current subscription info
  const { data: currentSubscription, isLoading: subscriptionLoading } = useQuery({
    queryKey: ['user-subscription', user?.id],
    queryFn: async () => {
      if (!user) return null;

      // Get user's role and tier from user_roles table
      const { data: roleData, error: roleError } = await backendClient
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      if (roleError) {
        console.error('Error fetching user role:', roleError);
        return { tier: 'free', credits_per_min: 2 };
      }

      // For now, assume all individual users are on free tier
      // This can be enhanced later with a subscription_users table
      return {
        tier: 'free',
        credits_per_min: 2
      } as UserSubscription;
    },
    enabled: !!user,
  });

  const handleUpgrade = async (tier: SubscriptionTier) => {
    setUpgradingToTier(tier.id);

    try {
      // Initiate Stripe subscription checkout
      await upgradeSubscription(tier.id);
      // Note: User will be redirected to Stripe, so no cleanup needed
    } catch (error) {
      console.error('Upgrade error:', error);
      setUpgradingToTier(null);
    }
  };

  if (tiersLoading || subscriptionLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription Plans</CardTitle>
          <CardDescription>Loading subscription options...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-muted rounded-lg"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentTier = currentSubscription?.tier || 'free';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-primary" />
          Subscription Plans
        </CardTitle>
        <CardDescription>
          Choose a subscription plan for better rates, included monthly credits, and exclusive benefits
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RoleUpgradeNotice />
        <div className="grid gap-4 md:grid-cols-3">
          {tiers.map((tier) => {
            const isCurrent = tier.tier_name.toLowerCase() === currentTier.toLowerCase();
            const isPro = tier.tier_name.toLowerCase() === 'pro';
            const isUpgrading = upgradingToTier === tier.id;
            const discountPercent = tier.credits_rate_multiplier < 1
              ? Math.round((1 - tier.credits_rate_multiplier) * 100)
              : 0;

            return (
              <Card
                key={tier.id}
                className={`relative ${
                  isPro ? 'border-primary shadow-lg ring-2 ring-primary/20' : ''
                } ${isCurrent ? 'border-accent' : ''}`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}

                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <Badge variant="secondary">Current Plan</Badge>
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {isPro && <Crown className="h-5 w-5 text-primary" />}
                    {tier.tier_name.toLowerCase() === 'free' && <Zap className="h-5 w-5 text-muted-foreground" />}
                    {tier.tier_name}
                  </CardTitle>
                  <CardDescription>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-foreground">
                        ${tier.monthly_price}
                      </span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-primary mr-2" />
                      <span>{tier.monthly_credits_included} credits/month</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-primary mr-2" />
                      <span>{tier.signup_bonus_credits} signup bonus</span>
                    </div>
                    {discountPercent > 0 && (
                      <div className="flex items-center text-sm font-medium text-primary">
                        <Check className="h-4 w-4 text-primary mr-2" />
                        <span>{discountPercent}% discount on credits</span>
                      </div>
                    )}
                    {tier.tier_name.toLowerCase() === 'free' && (
                      <div className="flex items-center text-sm">
                        <Check className="h-4 w-4 text-primary mr-2" />
                        <span>Pay-as-you-go pricing</span>
                      </div>
                    )}
                    {isPro && (
                      <>
                        <div className="flex items-center text-sm">
                          <Check className="h-4 w-4 text-primary mr-2" />
                          <span>Priority support</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Check className="h-4 w-4 text-primary mr-2" />
                          <span>Early access to features</span>
                        </div>
                      </>
                    )}
                  </div>

                  {tier.description && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {tier.description}
                    </p>
                  )}

                  {!isCurrent && (
                    <Button
                      className="w-full"
                      variant={isPro ? "default" : "outline"}
                      onClick={() => handleUpgrade(tier)}
                      disabled={isUpgrading || isProcessing}
                    >
                      {isUpgrading ? 'Redirecting to checkout...' :
                       tier.monthly_price === 0 ? 'Switch to Free' : `Upgrade to ${tier.tier_name}`}
                    </Button>
                  )}

                  {isCurrent && tier.monthly_price > 0 && (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => manageSubscription()}
                      disabled={isProcessing}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Subscription
                    </Button>
                  )}

                  {isCurrent && tier.monthly_price === 0 && (
                    <Button
                      className="w-full"
                      variant="secondary"
                      disabled
                    >
                      Current Plan
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium text-sm mb-2">💡 About Subscriptions</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Monthly credits reset each billing cycle</li>
            <li>• Purchased credits never expire</li>
            <li>• Pro tier members get discounted rates on additional credits</li>
            <li>• Cancel anytime - no long-term commitment</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
